import * as THREE from 'three'
import { ImportedModelResourceScopeCollection } from './ImportedModelResourceScopeCollection'

/**
 * 一个完整归档 revision 持有的 node 资源租约集合。
 *
 * scope 自身不改变场景；`retainExcludingRoots` 为未替换 node 创建新的 lease handle，
 * 使新旧 revision 短暂共存时共享资源不会提前归零。
 */
export class ImportedModelResourceScope implements PuppyCAD.ImportedModelResourceScopeHandle {
  /**
   * 为多个来源 scope 创建一个独立聚合作用域。
   *
   * @param sourceScopes 仍由其它 owner 持有的来源作用域。
   * @returns 对全部来源分别 retain 后形成的单一作用域句柄。
   */
  public static retainAll(
    sourceScopes: readonly PuppyCAD.ImportedModelResourceScopeHandle[],
  ): PuppyCAD.ImportedModelResourceScopeHandle {
    return ImportedModelResourceScopeCollection.retain(sourceScopes)
  }

  /**
   * 接管调用方已经取得所有权的多个 scope handle。
   *
   * @param ownedScopes 调用方不再单独释放的作用域句柄。
   * @returns 统一负责后续释放的聚合作用域。
   */
  public static adoptAll(
    ownedScopes: readonly PuppyCAD.ImportedModelResourceScopeHandle[],
  ): PuppyCAD.ImportedModelResourceScopeHandle {
    return ImportedModelResourceScopeCollection.adopt(ownedScopes)
  }

  /** 当前 scope 是否已经归还全部租约。 */
  private released = false

  /** 当前 scope 包含的稳定 node 身份。 */
  public readonly nodeIds: readonly string[]

  /** 尚未归还的 node 条目；释放后清空以断开 Object3D 与 lease 强引用。 */
  private entries: PuppyCAD.ImportedModelResourceScopeEntry[]

  /**
   * 创建归档资源作用域。
   *
   * @param entries node 身份、Object3D 根与独立 lease handle。
   */
  public constructor(entries: readonly PuppyCAD.ImportedModelResourceScopeEntry[]) {
    this.entries = [...entries]
    this.nodeIds = Object.freeze(entries.map((entry) => entry.nodeId))
  }

  /**
   * 为当前 scope 全部 node 创建新的所有权句柄。
   *
   * @returns 与当前 scope 引用同一 Object3D/GPU 资源的新 scope。
   */
  public retain(): ImportedModelResourceScope {
    this.assertAvailable()
    return this.createRetainedScope(this.entries)
  }

  /**
   * 保留不属于目标 Object3D 子树的 node。
   *
   * @param roots 被替换的一个或多个旧 Object3D 根。
   * @returns 只持有未替换 node 的新 scope。
   */
  public retainExcludingRoots(
    roots: readonly THREE.Object3D[],
  ): ImportedModelResourceScope {
    this.assertAvailable()
    const retainedEntries = this.entries.filter((entry) => !roots.some((root) => (
      entry.root === root || this.isDescendantOf(entry.root, root)
    )))
    return this.createRetainedScope(retainedEntries)
  }

  /** 归还当前 scope 的全部 node lease。 */
  public release(): void {
    if (this.released) {
      return
    }
    this.released = true
    const entries = this.entries
    this.entries = []
    const errors: unknown[] = []
    for (let index = entries.length - 1; index >= 0; index -= 1) {
      try {
        entries[index]?.lease.release()
      } catch (error) {
        errors.push(error)
      }
    }
    if (errors.length > 0) {
      throw new AggregateError(errors, '导入模型资源作用域释放失败')
    }
  }

  /**
   * 从指定 node 条目创建 retained scope。
   *
   * @param entries 需要保留的 node 条目。
   * @returns 每个条目都持有新 lease handle 的作用域。
   */
  private createRetainedScope(
    entries: readonly PuppyCAD.ImportedModelResourceScopeEntry[],
  ): ImportedModelResourceScope {
    const retainedEntries: PuppyCAD.ImportedModelResourceScopeEntry[] = []
    try {
      entries.forEach((entry) => {
        retainedEntries.push(Object.freeze({
          nodeId: entry.nodeId,
          root: entry.root,
          lease: entry.lease.retain(),
        }))
      })
      return new ImportedModelResourceScope(retainedEntries)
    } catch (retainError) {
      const rollbackErrors: unknown[] = []
      for (let index = retainedEntries.length - 1; index >= 0; index -= 1) {
        try {
          retainedEntries[index]?.lease.release()
        } catch (releaseError) {
          rollbackErrors.push(releaseError)
        }
      }
      if (rollbackErrors.length > 0) {
        throw new AggregateError(
          [retainError, ...rollbackErrors],
          '导入模型资源作用域 retain 失败且回滚不完整',
        )
      }
      throw retainError
    }
  }

  /**
   * 判断对象是否位于指定祖先子树内。
   *
   * @param object 待判断 node 根。
   * @param ancestor 被替换的场景子树根。
   * @returns object 是 ancestor 后代时返回 true。
   */
  private isDescendantOf(object: THREE.Object3D, ancestor: THREE.Object3D): boolean {
    let current = object.parent
    while (current) {
      if (current === ancestor) {
        return true
      }
      current = current.parent
    }
    return false
  }

  /** 已释放 scope 禁止再次 retain。 */
  private assertAvailable(): void {
    if (this.released) {
      throw new Error('已释放的导入模型资源作用域不能再次 retain')
    }
  }
}
